<script setup lang="ts">
import { reactive, ref, useId, watch, type ComponentPublicInstance } from 'vue';
import {
  autoUpdate,
  flip,
  offset,
  shift,
  size,
  useFloating,
} from '@floating-ui/vue';
import {
  Combobox,
  ComboboxLabel,
  ComboboxInput,
  ComboboxButton,
  ComboboxOptions,
  ComboboxOption,
} from '@headlessui/vue';
import { $t } from '@teachino/shared-i18n';
import HugeIcon from '../HugeIcon/HugeIcon.vue';
import { FORM_CONTROL_CLASS } from '../Input/form-control';
import type { TimeInputProps } from './time-input';

const props = defineProps<TimeInputProps>();
const labelId = useId();
const model = defineModel<string>({ required: true });
const emits = defineEmits<{
  focus: [];
  blur: [];
}>();
const fields = ['hours', 'minutes'] as const;
type Field = (typeof fields)[number];
const hoursPerDay = 24;
const minutesPerHour = 60;
const minuteInterval = 15;
const fieldWidth = 2;
const dropdownOffset = 4;
const viewportPadding = 8;
const createDropdown = () => {
  const anchor = ref<HTMLElement | null>(null);
  const panel = ref<HTMLElement | null>(null);
  const { floatingStyles } = useFloating(anchor, panel, {
    placement: 'bottom-start',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(dropdownOffset),
      flip(),
      shift({ padding: viewportPadding }),
      size({
        apply: ({ rects, elements }): void => {
          elements.floating.style.width = `${String(rects.reference.width)}px`;
        },
      }),
    ],
  });
  return { anchor, panel, floatingStyles };
};
const dropdowns = { hours: createDropdown(), minutes: createDropdown() };
const setDropdownElement = (
  field: Field,
  target: 'anchor' | 'panel',
  element: Element | ComponentPublicInstance | null,
): void => {
  dropdowns[field][target].value =
    element instanceof HTMLElement ? element : null;
};
const parts = reactive({ hours: '', minutes: '' });
const choosing = reactive({ hours: false, minutes: false });
const queries = reactive({ hours: '', minutes: '' });
const options = {
  hours: Array.from({ length: hoursPerDay }, (_, value) =>
    String(value).padStart(fieldWidth, '0'),
  ),
  minutes: Array.from({ length: minutesPerHour / minuteInterval }, (_, value) =>
    String(value * minuteInterval).padStart(fieldWidth, '0'),
  ),
};
watch(
  model,
  (value) => {
    const [hours = '', minutes = ''] = value.split(':');
    parts.hours = hours;
    parts.minutes = minutes;
  },
  { immediate: true },
);
const update = (): void => {
  model.value = `${parts.hours}:${parts.minutes}`;
};
const suggestions = (field: Field): string[] => {
  const query = queries[field].trim();
  if (!query) return options[field];
  const exact = query.padStart(fieldWidth, '0');
  return [
    ...options[field].filter((option) => option === exact),
    ...options[field].filter(
      (option) => option !== exact && option.startsWith(query),
    ),
  ];
};
const normalize = (field: Field): void => {
  const value = parts[field].trim();
  const limit = field === 'hours' ? hoursPerDay : minutesPerHour;
  if (/^\d{1,2}$/.test(value) && Number(value) < limit)
    parts[field] = value.padStart(fieldWidth, '0');
};
const input = (field: Field, event: Event): void => {
  parts[field] = (event.target as HTMLInputElement).value;
  queries[field] = parts[field];
  choosing[field] = false;
  update();
};
const blur = (field: Field): void => {
  normalize(field);
  update();
  emits('blur');
};
const select = (field: Field, value: string): void => {
  // Do not let the combobox's automatic selection on blur replace typed text.
  if (choosing[field]) parts[field] = value;
  normalize(field);
  update();
  queries[field] = '';
};
const navigate = (field: Field, event: KeyboardEvent): void => {
  if (['ArrowDown', 'ArrowUp', 'Enter'].includes(event.key))
    choosing[field] = true;
};
</script>

<template>
  <div
    role="group"
    :aria-labelledby="labelId"
    class="flex flex-wrap items-center gap-4"
  >
    <span :id="labelId" class="text-sm font-medium text-slate-700">
      {{ props.label }}
    </span>
    <div class="w-48 max-w-full">
      <div
        :class="[
          FORM_CONTROL_CLASS,
          'focus-within:border-primary-500 focus-within:ring-primary-500 flex items-center focus-within:ring-2',
        ]"
      >
        <template v-for="field in fields" :key="field">
          <div class="min-w-0 flex-1">
            <Combobox
              v-slot="{ open }"
              :model-value="parts[field]"
              @update:model-value="select(field, $event)"
            >
              <ComboboxLabel class="sr-only">
                {{
                  field === 'hours'
                    ? $t('time_input.hours')
                    : $t('time_input.minutes')
                }}
              </ComboboxLabel>
              <div
                :ref="(element) => setDropdownElement(field, 'anchor', element)"
                class="relative"
              >
                <ComboboxInput
                  class="w-full min-w-0 bg-white pr-8 text-center tabular-nums outline-none"
                  :aria-invalid="invalid || undefined"
                  inputmode="numeric"
                  autocomplete="off"
                  :maxlength="fieldWidth"
                  @input="input(field, $event)"
                  @keydown.capture="navigate(field, $event)"
                  @focus="
                    choosing[field] = false;
                    queries[field] = '';
                    emits('focus');
                  "
                  @blur="blur(field)"
                />
                <ComboboxButton
                  class="absolute inset-y-0 right-0 flex w-8 items-center justify-center text-slate-500"
                  :aria-label="
                    field === 'hours'
                      ? $t('time_input.hours')
                      : $t('time_input.minutes')
                  "
                  @click="queries[field] = ''"
                >
                  <HugeIcon icon="chevron_down" :size="16" />
                </ComboboxButton>
              </div>
              <Teleport to="body">
                <div
                  v-if="open"
                  :ref="
                    (element) => setDropdownElement(field, 'panel', element)
                  "
                  class="z-9999"
                  :style="dropdowns[field].floatingStyles.value"
                >
                  <ComboboxOptions
                    class="max-h-40 overflow-auto rounded-lg border border-slate-200 bg-white p-1 shadow-lg"
                    @mousedown.capture="choosing[field] = true"
                  >
                    <ComboboxOption
                      v-for="option in suggestions(field)"
                      :key="option"
                      v-slot="{ active, selected }"
                      :value="option"
                      as="template"
                    >
                      <li
                        class="cursor-pointer rounded px-4 py-2 text-sm text-slate-900 tabular-nums"
                        :class="{
                          'bg-primary-100': active,
                          'font-semibold': selected,
                        }"
                      >
                        {{ option }}
                      </li>
                    </ComboboxOption>
                  </ComboboxOptions>
                </div>
              </Teleport>
            </Combobox>
          </div>
          <span
            v-if="field === 'hours'"
            aria-hidden="true"
            class="px-2 text-slate-500"
            >:</span
          >
        </template>
      </div>
    </div>
  </div>
</template>
